import React, { useState, useEffect, useMemo, useRef } from 'react';
import useDeliveryOrders from '../hooks/useDeliveryOrders';
import DeliveryOrdersActions from '../components/delivery-orders/DeliveryOrdersActions';
import DeliveryOrdersTable from '../components/delivery-orders/DeliveryOrdersTable';
import { useErrorDialog } from '../context/errorDialogContext';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import DeliveryOrderViewModal from '../components/delivery-orders/DeliveryOrderViewModal';
import deliveryOrderService from '../services/deliveryOrderService';
import AssignDeliveryPersonnel from '../components/delivery-orders/AssignDeliveryPersonnel';
import StartTruckLoading from '../components/delivery-orders/StartTruckLoading';
import CompleteTruckLoading from '../components/delivery-orders/CompleteTruckLoading';
import ChallanSuccessModal from '../components/delivery-orders/ChallanSuccessModal';
import GenerateChallanModal from '../components/delivery-orders/GenerateChallanModal';
import UpdateTruckWeightModal from '../components/delivery-orders/UpdateTruckWeightModal';
import Pagination from '../components/common/Pagination';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'delivery_orders_page_size';

const DeliveryOrdersPage = () => {
    const { t } = useTranslation();
    const {
        orders,
        total,
        loading,
        error,
        fetchOrders,
        assignSupervisor,
        startLoading,
        generateChallan,
        markInTransit,
        markDelivered,
        fetchOrderById,
        markChallanPrinted,
    } = useDeliveryOrders();

    const [searchTerm, setSearchTerm] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });
    const searchDebounceRef = useRef(null);

    const [viewingOrder, setViewingOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    // Update Weight Modal State
    const [showUpdateWeightModal, setShowUpdateWeightModal] = useState(false);
    const [updateWeightOrderId, setUpdateWeightOrderId] = useState(null);
    const [updateWeightInitialData, setUpdateWeightInitialData] = useState(null);
    const [updateWeightLoading, setUpdateWeightLoading] = useState(false);

    // Assign Modal State
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignOrderId, setAssignOrderId] = useState(null);
    const [assignInitialValues, setAssignInitialValues] = useState(null);

    // Challan Success Modal State
    const [showChallanSuccessModal, setShowChallanSuccessModal] = useState(false);
    const [challanResultData, setChallanResultData] = useState(null);
    const [challanSubOrderId, setChallanSubOrderId] = useState(null);

    // Start Loading Modal State
    const [showStartLoadingModal, setShowStartLoadingModal] = useState(false);
    const [startLoadingOrderId, setStartLoadingOrderId] = useState(null);
    const [startLoadingInitialValues, setStartLoadingInitialValues] = useState(null);

    // Complete Loading Modal State
    const [showCompleteLoadingModal, setShowCompleteLoadingModal] = useState(false);
    const [completeLoadingOrderId, setCompleteLoadingOrderId] = useState(null);
    const [completeLoadingInitialValues, setCompleteLoadingInitialValues] = useState(null);

    // Generate Challan Modal State
    const [showGenerateChallanModal, setShowGenerateChallanModal] = useState(false);
    const [generateChallanOrderId, setGenerateChallanOrderId] = useState(null);

    const { showError } = useErrorDialog();
    const strings = t('deliveryOrdersModule', { returnObjects: true });

    // Debounce search: commit after 400ms, reset to page 1
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchTerm]);

    const { userRole } = useRole();
    const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;
    const isAccountUser = userRole === USER_ROLES.ACCOUNT_USER;

    // Fetch from server when page/committedSearch/statusFilter/isSupervisor/isAccountUser changes
    useEffect(() => {
        const skip = (currentPage - 1) * pageSize;
        const params = {
            skip,
            limit: pageSize,
        };
        params.status = statusFilter ? [statusFilter] : ['supervisor_assigned', 'loading', 'loaded', 'ready_for_challan'];

        if (committedSearch) {
            params.search = committedSearch;
        }

        fetchOrders(params);
    }, [fetchOrders, currentPage, committedSearch, statusFilter, isSupervisor, isAccountUser, pageSize]);

    useEffect(() => {
        if (error) showError(error, t('deliveryOrders.errors.errorTitle'));
    }, [error, showError, t]);

    // Only apply role-based filtering client-side; search/status are server-side
    const filteredOrders = useMemo(() => {
        const baseOrders = orders || [];

        // Sort by updated_at desc, fallback to created_at
        return [...baseOrders].sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
            const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
            return dateB - dateA;
        });
    }, [orders]);

    const handleStatusChange = (val) => {
        setStatusFilter(val);
        setCurrentPage(1);
    };

    const handleRefresh = async () => {
        const skip = (currentPage - 1) * pageSize;
        const params = {
            skip,
            limit: pageSize,
        };
        params.status = statusFilter ? [statusFilter] : ['supervisor_assigned', 'loading', 'loaded', 'ready_for_challan'];

        if (committedSearch) {
            params.search = committedSearch;
        }
        await fetchOrders(params);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleViewOrder = async (subOrder) => {
        if (subOrder.id) {
            setViewLoading(true);
            setViewingOrder(null);
            setShowViewModal(true);
            try {
                const result = await deliveryOrderService.getDeliverySubOrderById(subOrder.id);
                if (result.success && result.data) setViewingOrder(result.data);
                else showError(result.error || t('deliveryOrders.errors.failedToFetchOrderDetails'), t('deliveryOrders.errors.errorTitle'));
            } catch (err) {
                console.error('View order failed', err);
                showError(err.message || t('deliveryOrders.errors.failedToFetchOrderDetails'), t('deliveryOrders.errors.errorTitle'));
            } finally {
                setViewLoading(false);
            }
        }
    };

    const handleAssignSupervisor = (order) => {
        setAssignOrderId(order.id);
        setAssignInitialValues(order);
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async (orderId, payload) => {
        const result = await assignSupervisor(orderId, payload);
        if (result.success) {
            setShowAssignModal(false);
            setAssignOrderId(null);
            setAssignInitialValues(null);
            handleRefresh();
        } else {
            setAssignOrderId(null);
            setAssignInitialValues(null);
            handleRefresh();
        }
        return result;
    };

    const handleStartLoading = (order) => {
        setStartLoadingOrderId(order.id);
        setStartLoadingInitialValues(null);
        setShowStartLoadingModal(true);
    };

    const handleStartLoadingSubmit = async (orderId, payload) => {
        const result = await startLoading(orderId, payload);
        if (result.success) {
            setShowStartLoadingModal(false);
            setStartLoadingOrderId(null);
            setStartLoadingInitialValues(null);
            handleRefresh();
        }
        return result;
    };

    const handleCompleteLoading = (order) => {
        setCompleteLoadingOrderId(order.id);
        setCompleteLoadingInitialValues(null);
        setShowCompleteLoadingModal(true);
    };

    const handleCompleteLoadingSuccess = () => {
        handleRefresh();
    };

    const handleGenerateChallan = (orderId) => {
        setGenerateChallanOrderId(orderId);
        setShowGenerateChallanModal(true);
    };

    const handleConfirmGenerateChallan = async (orderId, payload) => {
        const result = await generateChallan(orderId, payload);
        if (result.success) {
            setShowGenerateChallanModal(false);
            setGenerateChallanOrderId(null);
            setChallanResultData(result.data || null);
            setChallanSubOrderId(orderId);
            setShowChallanSuccessModal(true);
            handleRefresh();
        } else {
            showError(result.error || t('deliveryOrders.errors.failedToGenerateChallan'), t('deliveryOrders.errors.errorTitle'));
        }
        return result;
    };

    const handlePrintChallan = async (order) => {
        const result = await fetchOrderById(order.id);
        if (result.success) {
            setChallanResultData(result.data || null);
            setChallanSubOrderId(order.id);
            setShowChallanSuccessModal(true);
        } else {
            showError(result.error || t('deliveryOrders.errors.failedToFetchOrderDetails'), t('deliveryOrders.errors.errorTitle'));
        }
    };

    const handleEditTruckWeight = async (order) => {
        if (!order.id) return;
        setUpdateWeightOrderId(order.id);
        setUpdateWeightLoading(true);
        setShowUpdateWeightModal(true);
        try {
            const result = await deliveryOrderService.getDeliverySubOrderById(order.id);
            if (result.success && result.data) {
                setUpdateWeightInitialData(result.data);
            } else {
                showError(result.error || strings.forms.updateTruckWeight.messages.error, t('deliveryOrders.errors.errorTitle'));
                setShowUpdateWeightModal(false);
            }
        } catch (err) {
            showError(err.message || strings.forms.updateTruckWeight.messages.error, t('deliveryOrders.errors.errorTitle'));
            setShowUpdateWeightModal(false);
        } finally {
            setUpdateWeightLoading(false);
        }
    };

    const handleUpdateTruckWeightSubmit = async (subOrderId, payload) => {
        setUpdateWeightLoading(true);
        try {
            if (!subOrderId) {
                showError(t('common.messages.subOrderIdNotFound'), t('common.alerts.errorTitle'));
                return;
            }

            const result = await deliveryOrderService.updateTruckWeights(subOrderId, payload);
            if (result.success) {
                setShowUpdateWeightModal(false);
                setUpdateWeightOrderId(null);
                setUpdateWeightInitialData(null);
                handleRefresh();
            } else {
                showError(result.error || strings.forms.updateTruckWeight.messages.error, t('deliveryOrders.errors.errorTitle'));
            }
        } catch (err) {
            showError(err.message || strings.forms.updateTruckWeight.messages.error, t('deliveryOrders.errors.errorTitle'));
        } finally {
            setUpdateWeightLoading(false);
        }
    };

    // Real total calculation: 
    // If we have fewer items than PAGE_LIMIT on the first page, the total is the number of items.
    // This is a safety measure in case the backend total includes filtered items.
    const realTotal = total;

    const totalPages = Math.ceil(realTotal / pageSize);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        {strings.page?.header?.title || 'Delivery Orders'}
                    </h1>
                    <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                        {strings.page?.header?.subtitle || 'Manage delivery sub-orders'}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <div
                        className="flex items-center gap-3 rounded-2xl border px-4 py-3"
                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                    >
                        <div
                            className="flex h-12 w-12 items-center justify-center rounded-full"
                            style={{
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                                color: UI_COLORS.BUTTONS.PRIMARY.background,
                            }}
                        >
                            <FontAwesomeIcon icon={faBox} />
                        </div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                {isAccountUser ? 'Ready for Challan' : strings.page.stats.total}
                            </p>
                            <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {realTotal}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <DeliveryOrdersActions
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                onRefresh={handleRefresh}
                refreshLoading={loading}
                statusValue={statusFilter}
                onStatusChange={handleStatusChange}
                userRole={userRole}
            />

            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
                <DeliveryOrdersTable
                    orders={filteredOrders}
                    loading={loading}
                    onViewOrder={handleViewOrder}
                    onAssignSupervisor={handleAssignSupervisor}
                    onStartLoading={handleStartLoading}
                    onCompleteLoading={handleCompleteLoading}
                    onGenerateChallan={handleGenerateChallan}
                    onPrintChallan={handlePrintChallan}
                    onMarkInTransit={markInTransit}
                    onMarkDelivered={markDelivered}
                    onEditTruckWeight={handleEditTruckWeight}
                    onSendChallanNotification={(id, type) => deliveryOrderService.sendChallanNotification(id, type)}
                    onRefresh={fetchOrders}
                    userRole={userRole}
                    enablePagination={false}
                />
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={realTotal}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total orders"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {showViewModal && (
                <DeliveryOrderViewModal
                    isOpen={showViewModal}
                    onClose={() => { setShowViewModal(false); setViewingOrder(null); }}
                    order={viewingOrder}
                    loading={viewLoading}
                />
            )}

            {showAssignModal && (
                <AssignDeliveryPersonnel
                    isOpen={showAssignModal}
                    orderId={assignOrderId}
                    initialValues={assignInitialValues}
                    onClose={() => { setShowAssignModal(false); setAssignOrderId(null); setAssignInitialValues(null); }}
                    onSubmit={handleAssignSubmit}
                />
            )}

            {showChallanSuccessModal && (
                <ChallanSuccessModal
                    isOpen={showChallanSuccessModal}
                    data={challanResultData}
                    onClose={() => { setShowChallanSuccessModal(false); setChallanResultData(null); setChallanSubOrderId(null); }}
                    onSendNotification={challanSubOrderId ? (type) => deliveryOrderService.sendChallanNotification(challanSubOrderId, type) : undefined}
                    onMarkChallanPrinted={challanSubOrderId ? () => markChallanPrinted(challanSubOrderId) : undefined}
                    userRole={userRole}
                    onEditSuccess={() => { fetchOrders(); }}
                />
            )}

            {showStartLoadingModal && (
                <StartTruckLoading
                    isOpen={showStartLoadingModal}
                    orderId={startLoadingOrderId}
                    initialValues={startLoadingInitialValues}
                    onClose={() => { setShowStartLoadingModal(false); setStartLoadingOrderId(null); setStartLoadingInitialValues(null); }}
                    onSubmit={handleStartLoadingSubmit}
                />
            )}

            {showCompleteLoadingModal && (
                <CompleteTruckLoading
                    isOpen={showCompleteLoadingModal}
                    orderId={completeLoadingOrderId}
                    initialOrder={completeLoadingInitialValues}
                    onClose={() => { setShowCompleteLoadingModal(false); setCompleteLoadingOrderId(null); setCompleteLoadingInitialValues(null); }}
                    onCompleted={handleCompleteLoadingSuccess}
                />
            )}

            {showGenerateChallanModal && (
                <GenerateChallanModal
                    isOpen={showGenerateChallanModal}
                    orderId={generateChallanOrderId}
                    onClose={() => { setShowGenerateChallanModal(false); setGenerateChallanOrderId(null); }}
                    onConfirm={handleConfirmGenerateChallan}
                    loading={loading}
                />
            )}

            {showUpdateWeightModal && (
                <UpdateTruckWeightModal
                    isOpen={showUpdateWeightModal}
                    orderId={updateWeightOrderId}
                    initialData={updateWeightInitialData}
                    loading={updateWeightLoading}
                    onClose={() => { setShowUpdateWeightModal(false); setUpdateWeightOrderId(null); setUpdateWeightInitialData(null); }}
                    onSubmit={handleUpdateTruckWeightSubmit}
                    onResetSuccess={() => { setShowUpdateWeightModal(false); setUpdateWeightOrderId(null); setUpdateWeightInitialData(null); fetchOrders(); }}
                />
            )}
        </div>
    );
};

export default DeliveryOrdersPage;
